import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
});

// Format FastAPI validation errors into a readable string
export function formatApiError(detail) {
    if (detail == null)
        return "Es konnte keine Verbindung hergestellt werden. Bitte prüfe deine Internetverbindung und versuche es erneut. Falls du gerade ein großes Foto hochgeladen hast, wähle ein kleineres oder versuche es im WLAN.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .join(" ");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

// ---------- Auth ----------
export const auth = {
    login: (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data),
    logout: () => api.post("/auth/logout").then((r) => r.data),
    me: () => api.get("/auth/me").then((r) => r.data),
};

// ---------- Memorials ----------
export const memorialsApi = {
    list: () => api.get("/memorials").then((r) => r.data),
    starColors: () => api.get("/memorials/star-colors").then((r) => r.data),
    get: (slug) => api.get(`/memorials/${slug}`).then((r) => r.data),
    image: (slug) => api.get(`/memorials/${slug}/image`).then((r) => r.data),
    checkCode: (code) => api.post("/memorials/code/check", { code }).then((r) => r.data),
    create: (formData) =>
        api
            .post("/memorials", formData, { headers: { "Content-Type": "multipart/form-data" } })
            .then((r) => r.data),
    lightCandle: (slug) => api.post(`/memorials/${slug}/candle`).then((r) => r.data),
    dropFeather: (slug) => api.post(`/memorials/${slug}/feather`).then((r) => r.data),
    getByToken: (token) => api.get(`/memorials/edit/${token}`).then((r) => r.data),
    updateByToken: (token, formData) =>
        api
            .put(`/memorials/edit/${token}`, formData, { headers: { "Content-Type": "multipart/form-data" } })
            .then((r) => r.data),
    deleteByToken: (token) => api.delete(`/memorials/edit/${token}`).then((r) => r.data),
    requestEditLink: (slug, email) =>
        api.post("/memorials/request-edit-link", { slug, email }).then((r) => r.data),
    listGuestbook: (slug) => api.get(`/memorials/${slug}/guestbook`).then((r) => r.data),
    addGuestbookEntry: (slug, body) =>
        api.post(`/memorials/${slug}/guestbook`, body).then((r) => r.data),
};

// ---------- Fund ----------
export const fundApi = {
    stats: () => api.get("/fund/stats").then((r) => r.data),
    cases: () => api.get("/fund/cases").then((r) => r.data),
    apply: (formData) =>
        api
            .post("/fund/apply", formData, { headers: { "Content-Type": "multipart/form-data" } })
            .then((r) => r.data),
};

// ---------- Adoptions ----------
export const adoptionsApi = {
    categories: () => api.get("/adoptions/categories").then((r) => r.data),
    list: (filters = {}) => api.get("/adoptions", { params: filters }).then((r) => r.data),
    get: (id) => api.get(`/adoptions/${id}`).then((r) => r.data),
    image: (id, idx) => api.get(`/adoptions/${id}/image/${idx}`).then((r) => r.data),
    contact: (id, body) => api.post(`/adoptions/${id}/contact`, body).then((r) => r.data),
    submit: (formData) =>
        api
            .post("/adoptions/submit", formData, { headers: { "Content-Type": "multipart/form-data" } })
            .then((r) => r.data),
    requestEditLink: (adoptionId, email) =>
        api.post("/adoptions/request-edit-link", { adoption_id: adoptionId, email }).then((r) => r.data),
    getByToken: (token) => api.get(`/adoptions/edit/${token}`).then((r) => r.data),
    messagesByToken: (token) => api.get(`/adoptions/edit/${token}/messages`).then((r) => r.data),
    updateByToken: (token, formData) =>
        api
            .put(`/adoptions/edit/${token}`, formData, { headers: { "Content-Type": "multipart/form-data" } })
            .then((r) => r.data),
    deleteByToken: (token) => api.delete(`/adoptions/edit/${token}`).then((r) => r.data),
};

// ---------- Payments ----------
export const paymentsApi = {
    createMemorialCheckout: (email, quantity = 1) =>
        api
            .post("/payments/memorial/checkout", {
                origin_url: window.location.origin,
                email,
                quantity,
            })
            .then((r) => r.data),
    createDonationCheckout: (amount, donorName, donorEmail) =>
        api
            .post("/payments/donation/checkout", {
                origin_url: window.location.origin,
                amount_eur: amount,
                donor_name: donorName || undefined,
                donor_email: donorEmail || undefined,
            })
            .then((r) => r.data),
    checkoutStatus: (sessionId) =>
        api.get(`/payments/checkout/status/${sessionId}`).then((r) => r.data),
};

// ---------- Shop ----------
export const shopApi = {
    products: () => api.get("/shop/products").then((r) => r.data),
    createCheckout: ({ productId, email, country }) =>
        api
            .post("/shop/checkout", {
                origin_url: window.location.origin,
                product_id: productId,
                email,
                country,
            })
            .then((r) => r.data),
};

// ---------- Admin ----------
export const adminApi = {
    memorials: () => api.get("/admin/memorials").then((r) => r.data),
    deleteMemorial: (id) => api.delete(`/admin/memorials/${id}`).then((r) => r.data),
    listCodes: () => api.get("/admin/codes").then((r) => r.data),
    createCodes: (count, note) => api.post("/admin/codes", { count, note }).then((r) => r.data),

    fundCases: (status) =>
        api.get("/fund/admin/cases", { params: status ? { status } : {} }).then((r) => r.data),
    updateCase: (id, body) => api.put(`/fund/admin/cases/${id}`, body).then((r) => r.data),
    deleteCase: (id) => api.delete(`/fund/admin/cases/${id}`).then((r) => r.data),
    ledger: () => api.get("/fund/admin/ledger").then((r) => r.data),
    addLedger: (body) => api.post("/fund/admin/ledger", body).then((r) => r.data),
    deleteLedger: (id) => api.delete(`/fund/admin/ledger/${id}`).then((r) => r.data),
    caseFile: (caseId, kind, idx) =>
        api.get(`/fund/admin/cases/${caseId}/file/${kind}/${idx}`).then((r) => r.data),

    platformRevenue: () => api.get("/payments/admin/revenue").then((r) => r.data),

    listAdoptions: () => api.get("/adoptions/admin/all").then((r) => r.data),
    createAdoption: (formData) =>
        api
            .post("/adoptions/admin", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            })
            .then((r) => r.data),
    updateAdoption: (id, body) => api.put(`/adoptions/admin/${id}`, body).then((r) => r.data),
    deleteAdoption: (id) => api.delete(`/adoptions/admin/${id}`).then((r) => r.data),
    messages: () => api.get("/adoptions/admin/messages/all").then((r) => r.data),
    markRead: (id) => api.post(`/adoptions/admin/messages/${id}/read`).then((r) => r.data),

    listGuestbook: () => api.get("/admin/guestbook").then((r) => r.data),
    updateGuestbook: (id, status) =>
        api.put(`/admin/guestbook/${id}`, { status }).then((r) => r.data),
    deleteGuestbook: (id) => api.delete(`/admin/guestbook/${id}`).then((r) => r.data),

    // ----- Shop -----
    listShopProducts: () => api.get("/shop/admin/products").then((r) => r.data),
    updateShopProduct: (id, body) =>
        api.put(`/shop/admin/products/${id}`, body).then((r) => r.data),
    uploadShopProductImage: (id, file) => {
        const fd = new FormData();
        fd.append("image", file);
        return api
            .put(`/shop/admin/products/${id}/image`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            })
            .then((r) => r.data);
    },
    listShopOrders: () => api.get("/shop/admin/orders").then((r) => r.data),
};
